import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Home from './pages/Home';
import About from './pages/About';
import Resume from './pages/Resume';
import Secret from './pages/Secret';
import Contact from './pages/Contact';
import Projects from './pages/Projects';
import Privacy from './pages/Privacy';
// CasinoUnavailable no longer used — casino is live
import { UIEffectsProvider } from './context/UIEffectsContext';
import { GameProvider } from './game/GameContext';
import { GameCanvas } from './game/GameCanvas';
import FilmGrainOverlay from './components/effects/FilmGrainOverlay';
import CustomCursor from './components/effects/CustomCursor';

import './styles/base/_app.scss';
import './styles/components/_suspense-skeleton.scss';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Puzzle1Page from './pages/puzzle/Puzzle1Page';
import Puzzle2Page from './pages/puzzle/Puzzle2Page';
import Puzzle3Page from './pages/puzzle/Puzzle3Page';

import ImageCaptioner from './pages/ImageCaptioner';

// Casino lobby and card games — lazy loaded
const EgyptianRatscrew = lazy(() => import('./pages/EgyptianRatscrew'));
const GinRummy = lazy(() => import('./pages/GinRummy'));
const Speed = lazy(() => import('./pages/Speed'));
const Casino = lazy(() => import('./pages/Casino'));
const CasinoWager = lazy(() => import('./pages/CasinoWager'));
const CasinoResults = lazy(() => import('./pages/CasinoResults'));

// PM dashboard — lazy: d3 (CompanyMap), heavy component tree (~20 components)
const PMLogin = lazy(() => import('./pages/pm/PMLogin'));
const PMDashboard = lazy(() => import('./pages/pm/PMDashboard'));

// Casino canvas game — lazy: canvas 2D game engine, Phaser-adjacent heavy code
const HoldEm = lazy(() => import('./pages/HoldEm'));

// Interview simulator — lazy: AI/socket-heavy components
const ColdCallSimulator = lazy(() => import('./pages/interview/ColdCallSimulator'));
const InterviewLogin = lazy(() => import('./pages/interview/InterviewLogin'));

// Calls Hub — lazy: dashboard + interview merge
const CallsHub = lazy(() => import('./pages/calls/CallsHub'));
const CallsDashboard = lazy(() => import('./pages/calls/dashboard/Dashboard').then(m => ({ default: m.Dashboard })));
const CRMDashboard = lazy(() => import('./pages/calls/crm/CRMDashboard'));

// Phase 32 D-05 — suppress film grain + custom cursor on fullscreen routes.
// Module-scope constant avoids recreation on every render (WR-02).
const FULLSCREEN_PREFIXES = ['/pm', '/casino', '/egyptian-ratscrew', '/gin-rummy', '/speed', '/holdem', '/interview', '/calls'];

const App = () => {
  const location = useLocation();

  const isFullscreenRoute = FULLSCREEN_PREFIXES.some((p) => location.pathname.startsWith(p));

  // Add page-specific class to the <body> tag and manage the custom-cursor-active
  // class. Uses classList.add/remove to avoid stomping classes owned by other effects (WR-01).
  useEffect(() => {
    const pageName = location.pathname.replace('/', '') || 'home';

    // Remove any stale page-* class before adding the new one
    const stalePageClass = Array.from(document.body.classList).find((c) => c.startsWith('page-'));
    if (stalePageClass) document.body.classList.remove(stalePageClass);
    document.body.classList.add(`page-${pageName}`);

    if (!isFullscreenRoute) {
      document.body.classList.add('custom-cursor-active');
    } else {
      document.body.classList.remove('custom-cursor-active');
    }
  }, [location, isFullscreenRoute]);

  return (
    <>
      <GameCanvas />
      {!isFullscreenRoute && <FilmGrainOverlay />}
      {!isFullscreenRoute && <CustomCursor />}
      {!isFullscreenRoute && <Navbar />}
      <Suspense fallback={<div className="suspense-skeleton"><div className="suspense-skeleton__bar" /></div>}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/secret" element={<Secret />} />
          <Route path="/resume" element={<Resume />} />
          <Route path="/puzzle1" element={<Puzzle1Page />} />
          <Route path="/puzzle2" element={<Puzzle2Page />} />
          <Route path="/puzzle3" element={<Puzzle3Page />} />
          <Route path="/image_captioner" element={<ImageCaptioner />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/pm/login" element={<PMLogin />} />
          <Route path="/pm" element={<PMDashboard />} />
          {/* Backwards-compatible redirects */}
          <Route path="/interview/login" element={<Navigate to="/calls" replace />} />
          <Route path="/interview" element={<Navigate to="/calls/interview" replace />} />
          {/* Calls Hub routes */}
          <Route path="/calls" element={<CallsHub />} />
          <Route path="/calls/interview" element={<ColdCallSimulator />} />
          <Route path="/calls/dashboard" element={<CallsDashboard />} />
          <Route path="/calls/crm" element={<CRMDashboard />} />
          {/* Casino routes */}
          <Route path="/casino" element={<Casino />} />
          <Route path="/casino/wager" element={<CasinoWager />} />
          <Route path="/casino/results" element={<CasinoResults />} />
          <Route path="/egyptian-ratscrew" element={<EgyptianRatscrew />} />
          <Route path="/gin-rummy" element={<GinRummy />} />
          <Route path="/speed" element={<Speed />} />
          <Route path="/holdem" element={<HoldEm />} />
        </Routes>
      </Suspense>
      {!isFullscreenRoute && <Footer />}
    </>
  );
};

const WrappedApp = () => (
  <Router>
    <UIEffectsProvider>
      <GameProvider>
        <App />
      </GameProvider>
    </UIEffectsProvider>
  </Router>
);

export default WrappedApp;
